<script setup>
import { computed, useAttrs } from 'vue';

const props = defineProps({
  variant: {
    type: String,
    default: 'solid',
    validator: value => ['outline', 'ghost', 'solid', 'soft'].includes(value),
  },
  size: {
    type: String,
    default: 'large',
    validator: value => ['medium', 'large'].includes(value),
  },
  icon: {
    type: String,
    default: '',
  },
  colorScheme: {
    type: String,
    default: 'primary',
    validator: value => ['primary', 'secondary', 'danger', 'success'].includes(value),
  },
  trailingIcon: {
    type: Boolean,
    default: false,
  },
});

const attrs = useAttrs();

const baseClasses = {
  outline: 'outline outline-1 -outline-offset-1',
  ghost: 'hover:text-600 active:text-600 focus:outline focus:outline-offset-1',
  solid:
    'hover:bg-700 active:bg-700 focus:outline focus:outline-offset-1 focus:outline-2',
  soft:
    'focus:outline focus:outline-offset-1 focus:outline-2',
};

const colorClass = computed(() => {
  if (attrs.disabled) {
    return 'bg-ash-200 text-ash-600 cursor-not-allowed';
  }

  const styleMap = {
    primary: {
      outline: `${baseClasses.outline} outline-primary-400 hover:text-primary-600 active:text-primary-600`,
      ghost: `${baseClasses.ghost} focus:outline-primary-400`,
      solid: `${baseClasses.solid} bg-primary-600 text-white focus:outline-primary-400`,
      soft: `${baseClasses.soft} bg-ash-100 text-ash-900 hover:bg-ash-100 active:bg-ash-100 focus:outline-primary-400 dark:bg-[#23282F] dark:text-ash-900`,
    },
    secondary: {
      outline: `${baseClasses.outline} outline-ash-400 hover:text-ash-900 active:text-ash-900 `,
      ghost: `${baseClasses.ghost} focus:outline-ash-400`,
      solid: `${baseClasses.solid} bg-ash-100 text-ash-900 focus:outline-ash-400`,
      soft: `${baseClasses.soft} bg-ash-100 text-ash-900 hover:bg-ash-100 active:bg-ash-100 focus:outline-ash-400 dark:bg-[#23282F]`,
    },
    danger: {
      outline: `${baseClasses.outline} outline-ruby-400 hover:text-ruby-600 active:text-ruby-600`,
      ghost: `${baseClasses.ghost} focus:outline-ruby-400`,
      solid: `${baseClasses.solid} bg-ruby-600 text-white focus:outline-ruby-400`,
      soft: `${baseClasses.soft} bg-ash-100 text-ruby-600 hover:bg-ash-100 active:bg-ash-100 focus:outline-ruby-400 dark:bg-[#23282F]`,
    },
    success: {
      outline: `${baseClasses.outline} outline-green-400 hover:text-green-600 active:text-green-600`,
      ghost: `text-green-500 ${baseClasses.ghost} focus:outline-green-400`,
      solid: `${baseClasses.solid} bg-green-500 text-white focus:outline-green-400`,
      soft: `${baseClasses.soft} bg-ash-100 text-green-600 hover:bg-ash-100 active:bg-ash-100 focus:outline-green-400 dark:bg-[#23282F] dark:text-green-800`,
    },
  };

  const schemeStyles = styleMap[props.colorScheme];
  return schemeStyles[props.variant] || schemeStyles.solid;
});

const sizeClass = computed(() => {
  if (props.size === 'medium') {
    return 'h-8 px-3 py-1.5 text-sm';
  }
  return 'h-10 px-4 py-2.5 text';
});

const buttonClasses = computed(() => [colorClass.value, sizeClass.value]);
</script>

<template>
  <button
    class="inline-flex items-center gap-1 text-sm font-medium reset-base rounded-lg w-fit"
    :class="buttonClasses"
    v-bind="$attrs"
  >
    <lucide-icon
      v-if="icon && !trailingIcon"
      size="1.16em"
      :name="icon"
      class="flex-shrink-0"
    />
    <span
      v-if="$slots.default"
      class="text-sm font-medium truncate ltr:text-left rtl:text-right"
    >
      <slot />
    </span>
    <lucide-icon
      v-if="icon && trailingIcon"
      size="1.16em"
      :name="icon"
      class="flex-shrink-0"
    />
  </button>
</template>